/**
 * GoogleSheetsSync Component
 * 
 * Modal component for syncing helpers from Google Sheets.
 * Checks if current year has Google Sheets configuration and allows syncing.
 * 
 * @param onClose - Callback function to close the modal
 * @param onSuccess - Callback function called when sync is successful
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import './GoogleSheetsSync.css';

interface GoogleSheetsSyncProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const GoogleSheetsSync: React.FC<GoogleSheetsSyncProps> = ({ onClose, onSuccess }) => {
  const { token, year } = useAuth();
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [checkingConfig, setCheckingConfig] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    message: string;
    synced?: number;
    updated?: number;
    skipped?: number;
    errors?: string[];
  } | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    checkConfiguration();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- checkConfiguration uses token/year from closure
  }, [year]);

  const checkConfiguration = async () => {
    try {
      setCheckingConfig(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/admin/years/maintenance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.years && year) {
        const yearData = response.data.years.find((y: { year: number }) => y.year === year);
        const configured = yearData?.google_sheets_configured || false;
        setIsConfigured(configured);
      } else {
        setIsConfigured(false);
      }
    } catch (err: any) {
      console.error('Error checking Google Sheets configuration:', err);
      setError('Fehler beim Prüfen der Konfiguration');
      setIsConfigured(false);
    } finally {
      setCheckingConfig(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError('');
      setSyncResult(null);

      const response = await axios.post(
        `${API_BASE_URL}/api/google-sheets/sync`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 60000 // 60 second timeout (sync can take longer)
        }
      );

      if (response.data.success) {
        setSyncResult({
          success: true,
          message: response.data.message || 'Synchronisierung erfolgreich',
          synced: response.data.synced || 0,
          updated: response.data.updated || 0,
          skipped: response.data.skipped || 0,
          errors: response.data.errors || []
        });
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setSyncResult({
          success: false,
          message: response.data.message || 'Synchronisierung fehlgeschlagen',
          errors: response.data.errors || []
        });
      }
    } catch (err: any) {
      console.error('Error syncing Google Sheets:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setSyncResult({
          success: false,
          message: 'Synchronisierung hat das Zeitlimit überschritten. Mögliche Ursachen: Netzwerkproblem oder Backend kann Google APIs nicht erreichen.',
          errors: []
        });
      } else {
        setSyncResult({
          success: false,
          message: err.response?.data?.message || err.message || 'Fehler beim Synchronisieren',
          errors: err.response?.data?.errors || []
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  const syncCompleted = syncResult?.success === true;

  return (
    <div className="google-sheets-sync-overlay" onClick={onClose}>
      <div className="google-sheets-sync-container" onClick={(e) => e.stopPropagation()}>
        <button className="google-sheets-sync-close" onClick={onClose}>×</button>
        <div className="google-sheets-sync-header">
          <h2 className="google-sheets-sync-title">Google Sheets Synchronisierung</h2>
        </div>
        <div className="google-sheets-sync-content">
          {checkingConfig ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div>Prüfe Konfiguration...</div>
            </div>
          ) : !isConfigured ? (
            <div>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                <strong>Google Sheets ist nicht konfiguriert</strong>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem' }}>
                  Für Jahr {year} wurde noch keine Google Sheets Konfiguration eingerichtet.
                </p>
              </div>
              <p style={{ marginBottom: '16px', fontSize: '0.9rem', color: '#666' }}>
                Bitte konfigurieren Sie Google Sheets in der Jahresverwaltung:
              </p>
              <ol style={{ marginLeft: '20px', fontSize: '0.9rem', color: '#666' }}>
                <li>Öffnen Sie "Jahre verwalten" im Verwaltungsmenü</li>
                <li>Klicken Sie auf "Konfigurieren" neben Jahr {year}</li>
                <li>Geben Sie die Google Sheet ID ein</li>
                <li>Laden Sie die Credentials JSON hoch</li>
                <li>Konfigurieren Sie das Field Mapping</li>
                <li>Speichern Sie die Konfiguration</li>
              </ol>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#d4edda', border: '1px solid #28a745', borderRadius: '4px' }}>
                <strong style={{ color: '#155724' }}>✓ Google Sheets ist konfiguriert</strong>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', color: '#155724' }}>
                  Jahr {year} ist für Google Sheets Synchronisierung eingerichtet.
                </p>
              </div>

              {syncResult && (
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  backgroundColor: syncResult.success ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${syncResult.success ? '#28a745' : '#dc3545'}`,
                  borderRadius: '4px'
                }}>
                  <strong style={{ color: syncResult.success ? '#155724' : '#721c24' }}>
                    {syncResult.success ? '✓ ' : '✗ '}
                    {syncResult.message}
                  </strong>
                  {(syncResult.synced !== undefined || syncResult.updated !== undefined || syncResult.skipped !== undefined) && syncResult.success && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', color: '#155724' }}>
                      {(syncResult.synced ?? 0) > 0 && `Neu: ${syncResult.synced}`}
                      {(syncResult.synced ?? 0) > 0 && ((syncResult.updated ?? 0) > 0 || (syncResult.skipped ?? 0) > 0) && ' | '}
                      {(syncResult.updated ?? 0) > 0 && `Aktualisiert: ${syncResult.updated}`}
                      {(syncResult.updated ?? 0) > 0 && (syncResult.skipped ?? 0) > 0 && ' | '}
                      {(syncResult.skipped ?? 0) > 0 && `Übersprungen: ${syncResult.skipped}`}
                    </p>
                  )}
                  {syncResult.errors && syncResult.errors.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <strong style={{ fontSize: '0.85rem' }}>Fehler/Warnungen:</strong>
                      <ul style={{ margin: '4px 0 0 20px', fontSize: '0.85rem' }}>
                        {syncResult.errors.slice(0, 10).map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                        {syncResult.errors.length > 10 && (
                          <li>... und {syncResult.errors.length - 10} weitere</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  backgroundColor: '#f8d7da',
                  border: '1px solid #dc3545',
                  borderRadius: '4px',
                  color: '#721c24'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSync}
                  disabled={syncing || syncCompleted}
                  style={{
                    padding: '10px 20px',
                    fontSize: '0.9rem',
                    backgroundColor: syncCompleted ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: syncing || syncCompleted ? 'not-allowed' : 'pointer',
                    opacity: syncing ? 0.6 : 1
                  }}
                  title={syncCompleted ? 'Synchronisierung abgeschlossen. Schließen und erneut öffnen oder Seite neu laden, um erneut zu synchronisieren.' : undefined}
                >
                  {syncing ? 'Synchronisiere...' : syncCompleted ? 'Synchronisiert' : '🔄 Synchronisieren'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleSheetsSync;
